import { Icon } from '@iconify/react'
import { useParams } from 'react-router'
import { useCallback, memo, useState } from 'react'
import { Badge, Tooltip } from 'antd'
import { nanoid } from 'nanoid'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { APP_ENVIRONMENT_CONSTANTS } from 'common/constants/appEnvironmentConstants'
import { env } from 'common/utils/env'
import { useAppSelector } from 'common/hooks/redux'
import { useCreateProcedureMutation } from 'features/Procedure/state/procedure.api'
import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import type { DrawingElement } from 'features/Procedure/state/procedure.type'
import { serializeDrawingDescriptionForApi } from 'features/Procedure/utils/drawingDescription.utils'
import { DocTemplatePreviewDialog } from '../doc-template-preview-dialog'
import { toPlainDynamicFieldSets } from '../manage-procedure/dynamic-fields.utils'

import styles from './procedure-actions.module.scss'
import classNames from 'classnames'

const IS_DEV_ENV = env.ENV === APP_ENVIRONMENT_CONSTANTS.DEVELOPMENT

// copies plain values only; the API sends them back wrapped as { value, label }
const dynamicFieldsForDuplicate = (procedure: ProcedureItem) => {
  const sets = toPlainDynamicFieldSets(procedure.dynamic_fields)
  return sets.length ? { dynamic_fields: sets } : {}
}

type Props = {
  procedure: ProcedureItem
  onScanProducts: (procedure: ProcedureItem) => void
  onNavigateToForms?: (documentId?: number | null) => void
}

export const ProcedureActions = memo(({ procedure, onScanProducts, onNavigateToForms }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )
  const selectedPatientId = useAppSelector((state) => state.procedureReducer.selectedPatientId)
  const { setNotification } = useNotification()
  const [isDocPreviewOpen, setIsDocPreviewOpen] = useState(false)
  const [isDuplicateTooltipOpen, setIsDuplicateTooltipOpen] = useState(false)
  const [isDuplicateWithDrawingsTooltipOpen, setIsDuplicateWithDrawingsTooltipOpen] =
    useState(false)

  const [createProcedure, { isLoading: isDuplicating }] = useCreateProcedureMutation({
    fixedCacheKey: 'create-procedure',
  })

  const docTemplate = procedure?.doc_template
  const hasDocTemplate = Boolean(docTemplate)
  const hasDocToSign = docTemplate?.has_doc_to_sign === 1
  const isDocSigned = docTemplate?.has_doc_to_sign === 0
  const canOpenDocPreview = hasDocToSign && typeof docTemplate?.id === 'number'

  const handleOpenDocPreview = useCallback(() => {
    if (canOpenDocPreview) {
      setIsDocPreviewOpen(true)
    }
  }, [canOpenDocPreview])

  const handleCloseDocPreview = useCallback(() => {
    setIsDocPreviewOpen(false)
  }, [])

  const handleOpenSignedDocument = useCallback(() => {
    onNavigateToForms?.(docTemplate?.document_id ?? null)
  }, [onNavigateToForms, docTemplate?.document_id])

  const handleScan = useCallback(() => {
    onScanProducts(procedure)
  }, [onScanProducts, procedure])

  const handleDuplicate = useCallback(async () => {
    try {
      if (!siteId || !selectedEncounterUid) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing site or encounter information',
        })
        return
      }

      const duplicatedProcedure = {
        code: procedure.code,
        code_type: procedure.code_type,
        procedure_units: procedure.procedure_units,
        units: procedure.units,
        fee: procedure.fee,
        provider_id: procedure.provider_id,
        justify_codes: procedure.justify_codes,
        ...dynamicFieldsForDuplicate(procedure),
      }

      const result = await createProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        data: {
          procedures: [duplicatedProcedure],
        },
      }).unwrap()

      if (result && typeof result === 'object' && 'success' in result) {
        const responseResult = result as { success: boolean; error?: string; statusCode?: number }
        if (!responseResult.success) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: responseResult.error || 'Failed to duplicate procedure',
          })
          return
        }
      }
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: error?.data?.message || 'Failed to duplicate procedure',
      })
    }
  }, [siteId, selectedEncounterUid, procedure, createProcedure, setNotification])

  const handleDuplicateWithDrawings = useCallback(async () => {
    try {
      if (!siteId || !selectedEncounterUid) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing site or encounter information',
        })
        return
      }

      const sourceDrawings = procedure?.drawings?.drawingElements ?? []
      const clonedDrawings: DrawingElement[] = sourceDrawings.map((el) => {
        const {
          id: _serverId,
          procedureId: _procedureId,
          clientKey: _clientKey,
          isModified: _isModified,
          ...rest
        } = el
        return {
          ...rest,
          id: nanoid(),
          description: serializeDrawingDescriptionForApi(el.description, el.drawMode),
        } as DrawingElement
      })

      const duplicatedProcedure = {
        code: procedure.code,
        code_type: procedure.code_type,
        procedure_units: procedure.procedure_units,
        units: procedure.units,
        fee: procedure.fee,
        provider_id: procedure.provider_id,
        justify_codes: procedure.justify_codes,
        ...dynamicFieldsForDuplicate(procedure),
        drawings: clonedDrawings,
      }

      const result = await createProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        data: {
          procedures: [duplicatedProcedure],
        },
      }).unwrap()

      if (result && typeof result === 'object' && 'success' in result) {
        const responseResult = result as { success: boolean; error?: string; statusCode?: number }
        if (!responseResult.success) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: responseResult.error || 'Failed to duplicate procedure with drawings',
          })
          return
        }
      }
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: error?.data?.message || 'Failed to duplicate procedure with drawings',
      })
    }
  }, [siteId, selectedEncounterUid, procedure, createProcedure, setNotification])

  const isLoading = isDuplicating

  const badgeCount = procedure?.product_labels?.length || 0

  return (
    <>
      <div className={styles.container}>
        {/* <Tooltip title='Delete procedure'>
          <button
            className={styles.iconButton}
            data-is-delete='true'
            onClick={handleDeleteClick}
            disabled={isLoading}>
            <Icon icon='mdi:trash-can-outline' width={20} height={20} />
          </button>
        </Tooltip> */}
        <Tooltip
          title='Duplicate procedure'
          open={isDuplicateTooltipOpen}
          onOpenChange={setIsDuplicateTooltipOpen}>
          <button
            className={styles.iconButton}
            onClick={() => {
              setIsDuplicateTooltipOpen(false)
              handleDuplicate()
            }}
            disabled={isLoading}>
            <Icon icon='ion:duplicate-outline' width={20} height={20} />
          </button>
        </Tooltip>
        {/* {IS_DEV_ENV && (
          <Tooltip
            title='Duplicate procedure + drawings (dev only)'
            open={isDuplicateWithDrawingsTooltipOpen}
            onOpenChange={setIsDuplicateWithDrawingsTooltipOpen}>
            <button
              className={styles.iconButton}
              onClick={() => {
                setIsDuplicateWithDrawingsTooltipOpen(false)
                handleDuplicateWithDrawings()
              }}
              disabled={isLoading}>
              <Icon icon='mdi:content-duplicate' width={20} height={20} />
            </button>
          </Tooltip>
        )} */}
        {/* <Tooltip title='Edit procedure'>
          <button className={styles.iconButton} onClick={handleEdit} disabled={isLoading}>
            <Icon icon='mdi:pencil-outline' width={20} height={20} />
          </button>
        </Tooltip> */}
        <Tooltip title='Scan procedure' placement='topLeft'>
          <Badge
            showZero
            color={badgeCount > 0 ? 'var(--success)' : 'var(--danger)'}
            count={badgeCount}
            size='small'>
            <button className={styles.iconButton} onClick={handleScan} disabled={isLoading}>
              <Icon icon='bi:qr-code-scan' width={20} height={20} />
            </button>
          </Badge>
        </Tooltip>
        {hasDocTemplate && (
          <Tooltip
            title={isDocSigned ? 'View signed document' : 'eSign document'}
            placement='topLeft'>
            <button
              className={classNames(styles.iconButton, {
                [styles.iconButtonSignWarning]: hasDocToSign,
                [styles.iconButtonSignSuccess]: isDocSigned,
              })}
              disabled={isLoading || (hasDocToSign && !canOpenDocPreview)}
              onClick={isDocSigned ? handleOpenSignedDocument : handleOpenDocPreview}>
              <Icon icon='mdi:sign' width={20} height={20} color='var(--primary-20)' />
            </button>
          </Tooltip>
        )}
      </div>
      {isDocPreviewOpen && selectedPatientId && selectedEncounterUid && docTemplate?.id && (
        <DocTemplatePreviewDialog
          isVisible={isDocPreviewOpen}
          onClose={handleCloseDocPreview}
          patientId={selectedPatientId}
          encounterUid={selectedEncounterUid}
          templateId={docTemplate.id}
          title={docTemplate.title}
          onSubmitSuccess={handleCloseDocPreview}
        />
      )}
    </>
  )
})
